import { escapeHTML } from '../../utils/escape.js';
import { formatFechaHora } from '../../utils/formatters.js';

/**
 * Admin "Reportes" section (openspec/changes/staff-reportes/spec.md B3/B4):
 * messages from the staff, newest first, filtered by Nuevos / Leídos /
 * Archivados. Pure render; `js/reportes.js` owns state and listeners.
 */

const FILTROS = [
    { filtro: 'nuevos', label: 'Nuevos' },
    { filtro: 'leidos', label: 'Leídos' },
    { filtro: 'archivados', label: 'Archivados' },
];

const VACIO = {
    nuevos: 'No hay mensajes nuevos.',
    leidos: 'No hay mensajes leídos.',
    archivados: 'No hay mensajes archivados.',
};

const BOTON = 'shrink-0 px-4 py-2 rounded-xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors disabled:opacity-60';

/**
 * @param {{ filtro: 'nuevos'|'leidos'|'archivados', reporte: Record<string, any>, ocupado: boolean }} params
 * @returns {string}
 */
function acciones({ filtro, reporte, ocupado }) {
    const id = escapeHTML(reporte.id);
    const disabled = ocupado ? 'disabled' : '';
    const leido =
        filtro === 'nuevos'
            ? `<button type="button" data-action="reporte-leido" data-id="${id}" ${disabled} class="${BOTON}">Marcar leído</button>`
            : '';
    const archivar =
        filtro === 'archivados'
            ? ''
            : `<button type="button" data-action="reporte-archivar" data-id="${id}" ${disabled} class="${BOTON}">Archivar</button>`;
    return leido || archivar ? `<div class="flex flex-wrap gap-2">${leido}${archivar}</div>` : '';
}

/**
 * @param {{
 *   uiState: { status: 'loading' | 'error' | 'ready', mensaje?: string },
 *   filtro: 'nuevos'|'leidos'|'archivados',
 *   reportes: Array<Record<string, any>>,
 *   marcando?: string | null,
 * }} props
 * @returns {string}
 */
export function AdminReportesView({ uiState, filtro, reportes, marcando = null }) {
    const tabs = FILTROS.map(({ filtro: f, label }) => {
        const activo = f === filtro;
        const clases = activo ? 'bg-brand text-white shadow-lg' : 'bg-surface border border-border-subtle text-text-muted hover:text-brand';
        return `<button type="button" data-action="reportes-filtro" data-filtro="${f}" aria-pressed="${activo}"
            class="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-colors ${clases}">${label}</button>`;
    }).join('');

    let cuerpo;
    if (uiState.status === 'loading') {
        cuerpo = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-2xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando mensajes...</p>
            </div>`;
    } else if (uiState.status === 'error') {
        cuerpo = `
            <div class="flex flex-col items-center justify-center py-16 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">${escapeHTML(uiState.mensaje ?? 'No se pudieron cargar los mensajes.')}</p>
                <button type="button" data-action="reportes-reintentar" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>`;
    } else if (reportes.length === 0) {
        cuerpo = `<p class="py-12 text-center text-sm text-text-muted">${VACIO[filtro] ?? VACIO.nuevos}</p>`;
    } else {
        const items = reportes
            .map(
                (r) => `
                <article data-reporte-id="${escapeHTML(r.id)}" class="flex flex-col gap-3 py-5 border-b border-border-subtle last:border-b-0">
                    <header class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <p class="text-base font-black text-text-primary">${escapeHTML(r.empleados?.nombre ?? 'Empleado')}</p>
                        <time datetime="${escapeHTML(r.creado_at)}" class="text-[11px] font-bold tabular-nums text-text-muted">${escapeHTML(formatFechaHora(r.creado_at))}</time>
                    </header>
                    <p class="text-sm text-text-primary leading-relaxed whitespace-pre-wrap break-words">${escapeHTML(r.texto)}</p>
                    ${acciones({ filtro, reporte: r, ocupado: marcando === r.id })}
                </article>`,
            )
            .join('');
        cuerpo = `<div class="bg-surface border border-border-subtle rounded-2xl px-5">${items}</div>`;
    }

    return `
        <section data-reportes-admin>
            <header class="mb-6">
                <h2 class="text-2xl font-black uppercase tracking-tight text-text-primary">Reportes del staff</h2>
                <p class="mt-1 text-sm text-text-muted max-w-2xl">Mensajes que el equipo te mandó desde el panel de staff, firmados con su PIN.</p>
            </header>
            <div role="group" aria-label="Filtrar mensajes" class="mb-5 flex flex-wrap gap-2">${tabs}</div>
            ${cuerpo}
        </section>
    `;
}
